/**
 * Horizontal marquee for sticky add-to-cart offer strip.
 * Fills the viewport with repeated offers, then duplicates the track for a seamless -50% loop.
 */

const SPEED_MAP = {
  1: '40s',
  2: '30s',
  3: '25s',
  4: '20s',
  5: '15s',
};

const MIN_FILL_WIDTH = 1500;

/**
 * @param {() => void} fn
 * @param {number} wait
 */
function debounce(fn, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timer;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(fn, wait);
  };
}

/**
 * @param {HTMLElement} content
 * @param {string} originalHTML
 * @param {number} minWidth
 */
function fillContentToWidth(content, originalHTML, minWidth) {
  content.innerHTML = originalHTML;

  const setWidth = content.scrollWidth;
  if (setWidth === 0) return;

  const copiesNeeded = Math.max(1, Math.ceil(minWidth / setWidth));

  for (let i = 1; i < copiesNeeded; i++) {
    const fragment = document.createElement('div');
    fragment.innerHTML = originalHTML;
    Array.from(fragment.children).forEach((child) => {
      content.appendChild(child);
    });
  }
}

/**
 * @param {HTMLElement} wrapper
 * @param {HTMLElement} sourceContent
 */
function applyAnimation(wrapper, sourceContent) {
  const speed = sourceContent.dataset.speed || '3';
  const direction = sourceContent.dataset.direction || 'left';

  wrapper.style.setProperty('--som-speed', SPEED_MAP[/** @type {keyof typeof SPEED_MAP} */ (speed)] || '25s');
  wrapper.style.animationDirection = direction === 'right' ? 'reverse' : 'normal';
}

/**
 * @param {HTMLElement} root
 */
function buildStickyOffersMarquee(root) {
  const container = root.querySelector('.sticky-offers-marquee__container');
  const wrapper = root.querySelector('.sticky-offers-marquee__wrapper');
  const content = root.querySelector('.sticky-offers-marquee__content');

  if (!container || !wrapper || !content) return;

  if (!root._somOriginalHTML) {
    root._somOriginalHTML = content.innerHTML.trim();
  }

  const originalHTML = root._somOriginalHTML;
  if (!originalHTML) return;

  wrapper.querySelectorAll('.sticky-offers-marquee__content[aria-hidden="true"]').forEach((el) => el.remove());

  fillContentToWidth(content, originalHTML, Math.max(container.offsetWidth, MIN_FILL_WIDTH));

  const clone = /** @type {HTMLElement} */ (content.cloneNode(true));
  clone.setAttribute('aria-hidden', 'true');
  wrapper.appendChild(clone);

  applyAnimation(wrapper, content);
  void wrapper.offsetWidth;

  if (typeof window.syncAnnouncementBarToStickyMarquee === 'function') {
    window.syncAnnouncementBarToStickyMarquee();
  }
}

/**
 * @param {HTMLElement} root
 */
function initStickyOffersMarquee(root) {
  if (root._somInitialized) return;
  root._somInitialized = true;

  const build = () => buildStickyOffersMarquee(root);
  build();

  const container = root.querySelector('.sticky-offers-marquee__container');
  if (container) {
    const onResize = debounce(build, 150);
    const observer = new ResizeObserver(onResize);
    observer.observe(container);
    root._somResizeObserver = observer;
  }
}

function setupStickyOffersMarquees() {
  document.querySelectorAll('.sticky-offers-marquee').forEach((el) => {
    initStickyOffersMarquee(/** @type {HTMLElement} */ (el));
  });
}

window.initStickyOffersMarquee = initStickyOffersMarquee;
window.rebuildStickyOffersMarquee = buildStickyOffersMarquee;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', setupStickyOffersMarquees);
} else {
  setupStickyOffersMarquees();
}

document.addEventListener('shopify:section:load', setupStickyOffersMarquees);

if (document.body) {
  new MutationObserver(setupStickyOffersMarquees).observe(document.body, {
    childList: true,
    subtree: true,
  });
}
